import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Loader2, Download } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { adminService, RegisteredUser } from "../../services/adminService";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";
import * as XLSX from "xlsx";

const ExportUsers = () => {
  const [users, setUsers] = useState<RegisteredUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(
    new Set()
  );
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });

  const fetchUsers = useCallback(
    async (page = 1, search = "") => {
      try {
        setLoading(true);
        const data = await adminService.getRegisteredUsers({
          page: page,
          page_size: pagination.pageSize,
          search: search,
          ordering: "id",
        });
        setUsers(data?.results ?? []);
        setPagination(prev => ({
          ...prev,
          count: data?.count ?? 0,
          page: page,
        }));
      } catch (error: any) {
        console.error("Error fetching users:", error);
        showErrorToast(error, "Failed to load users for export.");
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUsers(1, searchTerm);
  };

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      const allOnPage = users.map(u => u.id);
      setSelectedUserIds(
        new Set([...Array.from(selectedUserIds), ...allOnPage])
      );
    } else {
      const remaining = Array.from(selectedUserIds).filter(
        id => !users.some(u => u.id === id)
      );
      setSelectedUserIds(new Set(remaining));
    }
  };

  const toggleSelectUser = (id: number, checked: boolean) => {
    const next = new Set(selectedUserIds);
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    setSelectedUserIds(next);
  };

  const handleExport = () => {
    if (selectedUserIds.size === 0) {
      toast.error("Please select at least one user to export.");
      return;
    }

    // Filter from current users if they are there, otherwise we might need a way to fetch all selected?
    // For now, if users are selected across pages, we might have an issue if we only have current 'users' state.
    // However, if we store the USER objects OR fetch them by IDs.
    // Given the scale, it's better to fetch selected users or assume we only export what's on screen if selected.
    // But usually 'selected across pages' is expected.

    // Simplest approach for now: Export the users currently in the `users` list that are selected.
    // If the user wants to export ALL, they should maybe have a "Select All (Entire Database)" option.
    // But I'll implement it such that it exports whatever is in the `selectedUserIds` Set,
    // but I can only export the data I have.

    const selectedUsersData = users.filter(u => selectedUserIds.has(u.id));

    if (selectedUsersData.length === 0) {
      toast.error(
        "The selected records are not on the current page. Please stay on the page where users are listed to export them."
      );
      return;
    }

    const exportData = selectedUsersData.map(user => ({
      ID: user.id,
      Name:
        (user.full_name ??
          `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()) ||
        "N/A",
      Email: user.email,
      Phone: user.phone ?? user.phone_number ?? "-",
      City: user.city ?? "-",
      State: user.state ?? "-",
      Country: user.country ?? "-",
      "User Type": user.user_type ?? "User",
      Status: user.is_active !== false ? "Active" : "Inactive",
      "Date Joined": user.date_joined
        ? new Date(user.date_joined).toLocaleDateString()
        : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Users");

    XLSX.writeFile(
      workbook,
      `User_Export_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    toast.success(`${selectedUsersData.length} users exported to Excel!`);
  };

  const totalPages = Math.ceil(pagination.count / pagination.pageSize);
  const isAllOnPageSelected =
    users.length > 0 && users.every(u => selectedUserIds.has(u.id));

  return (
    <AdminLayout>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground md:text-3xl">
              Export Users
            </h2>
            <p className="text-sm text-muted-foreground">
              Select users and download as an Excel file.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleExport}
              disabled={selectedUserIds.size === 0}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download Excel ({selectedUserIds.size})
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-lg border bg-card p-4 md:flex-row md:items-center md:justify-between">
          <form
            onSubmit={handleSearch}
            className="flex w-full max-w-sm items-center gap-2"
          >
            <div className="relative w-full">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search users..."
                className="w-full pl-9"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <Button
              type="submit"
              variant="secondary"
              className="shrink-0 bg-[#533a1d] text-white hover:bg-[#533a1d]/90"
            >
              Search
            </Button>
          </form>
          <div className="text-sm text-muted-foreground">
            {selectedUserIds.size} users selected
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="w-12 px-4 py-3 text-left">
                    <Checkbox
                      checked={isAllOnPageSelected}
                      onCheckedChange={checked =>
                        toggleSelectAll(checked === true)
                      }
                    />
                  </th>
                  <th className="w-16 px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading && users?.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <p>Loading users...</p>
                      </div>
                    </td>
                  </tr>
                ) : users?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-muted-foreground"
                    >
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users?.map(user => (
                    <tr
                      key={user.id}
                      className="transition-colors hover:bg-muted/30"
                    >
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={selectedUserIds.has(user.id)}
                          onCheckedChange={checked =>
                            toggleSelectUser(user.id, checked === true)
                          }
                        />
                      </td>
                      <td className="px-4 py-3 font-medium">{user.id}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span>
                            {user.full_name ??
                              ((user.first_name ?? user.last_name)
                                ? `${user.first_name ?? ""} ${user.last_name ?? ""}`
                                : "N/A")}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{user.email}</td>
                      <td className="px-4 py-3">{user.phone_number ?? "-"}</td>
                      <td className="px-4 py-3">
                        {[user.city, user.state, user.country]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            user.is_active !== false
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                          }`}
                        >
                          {user.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <div className="text-sm text-muted-foreground">
            {pagination.count > 0 ? (
              <>
                Showing{" "}
                <span className="font-medium text-foreground">
                  {(pagination.page - 1) * pagination.pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium text-foreground">
                  {Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.count
                  )}
                </span>{" "}
                of{" "}
                <span className="font-medium text-foreground">
                  {pagination.count}
                </span>{" "}
                results
              </>
            ) : (
              "No results to show"
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchUsers(1, searchTerm)}
              disabled={pagination.page === 1 || loading}
            >
              « First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchUsers(pagination.page - 1, searchTerm)}
              disabled={pagination.page === 1 || loading}
            >
              ‹ Back
            </Button>
            <div className="flex h-9 items-center rounded-md border bg-muted/50 px-4 text-sm font-medium">
              Page {pagination.page} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchUsers(pagination.page + 1, searchTerm)}
              disabled={pagination.page >= totalPages || loading}
            >
              Next ›
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchUsers(totalPages, searchTerm)}
              disabled={pagination.page >= totalPages || loading}
            >
              Last »
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ExportUsers;
